import { ReactNode, useState } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { Spinner } from '../../feedback';
import { Box, Flex } from '../../layout';
import { Icon, IconName } from '../../medias';
import { Paragraph } from '../../typography';
import { Dropzone } from './styled';

type DocumentInputProps = {
  name: string;
  filename?: string;
  accept?: Accept;
  allowedFormatText?: ReactNode;
  onDrop: <T extends File>(
    acceptedFiles: T[],
    fileRejections: FileRejection[]
  ) => void;
  error?: string | null;
  onReset?: () => void;
  onFileClick?: () => void;
  description?: ReactNode;
  ctaText?: ReactNode;
  isLoading?: boolean;
};

export function DocumentInput(props: DocumentInputProps): JSX.Element {
  const {
    name,
    onDrop,
    accept,
    allowedFormatText,
    error,
    onReset,
    filename,
    onFileClick,
    description,
    ctaText,
    isLoading
  } = props;

  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);

  const onInternalDrop = async <T extends File>(
    acceptedFiles: T[],
    fileRejections: FileRejection[]
  ): Promise<void> => {
    setAcceptedFiles(acceptedFiles);

    try {
      await onDrop(acceptedFiles, fileRejections);
    } catch {
      setAcceptedFiles([]);
    }
  };

  const file = acceptedFiles[0];

  const currentFilename = filename ?? file?.name;

  const onInternalFileClick = (): void => {
    if (filename) {
      onFileClick?.();
      return;
    }

    if (!file) {
      return;
    }

    const url = window.URL.createObjectURL(file);
    const a = document.createElement('a');

    a.href = url;
    a.download = file.name;
    a.setAttribute('style', 'display: none;');

    document.body.appendChild(a);

    a.click();
    a.remove();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    useFsAccessApi: false, // does not work with playwright. https://github.com/microsoft/playwright/issues/8850
    onDrop: onInternalDrop,
    accept: accept ?? {
      'application/pdf': ['.pdf']
    }
  });

  return (
    <Box>
      <Dropzone
        data-testid="input-dropzone"
        {...getRootProps()}
        isDragActive={isDragActive}
        hasFile={!!currentFilename}
        {...(currentFilename ? { onClick: onInternalFileClick } : {})}
      >
        <input
          {...getInputProps({
            name,
            type: 'file'
          })}
        />
        <DropzoneContent
          onReset={(): void => {
            setAcceptedFiles([]);
            onReset?.();
          }}
          currentFilename={currentFilename}
          description={description}
          ctaText={ctaText}
          isLoading={isLoading}
        />
      </Dropzone>

      {allowedFormatText || error ? (
        <Box marginTop={'spacing_unit.1'}>
          {allowedFormatText ? (
            <Paragraph colorVariant="lighter">{allowedFormatText}</Paragraph>
          ) : null}
          {error ? (
            <Paragraph style={{ color: 'red' }}>{error}</Paragraph>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

type DropzoneContentProps = {
  currentFilename?: string;
  onReset: () => void;
  description?: ReactNode;
  ctaText?: ReactNode;
  isLoading?: boolean;
};

function DropzoneContent(props: DropzoneContentProps): JSX.Element {
  const { currentFilename, onReset, description, ctaText, isLoading } = props;

  if (isLoading) {
    return (
      <Flex
        align={'center'}
        justify={'center'}
        padding={'20px'}
        height={'96.8px'}
      >
        <Spinner />
      </Flex>
    );
  }

  if (!currentFilename) {
    return (
      <>
        <Icon
          name={IconName.FileUploadIcon}
          width={'2em'}
          height={'2em'}
          color={'palette_blue.100'}
        />

        <Paragraph>
          {description ?? (
            <>
              Drag a file here to upload
              <br />
              or
            </>
          )}
        </Paragraph>
        <Paragraph>{ctaText ?? <>Select a file</>}</Paragraph>
      </>
    );
  }

  return (
    <>
      <span
        data-testid="document-input-close-button"
        onClick={(e): void => {
          e.stopPropagation();
          onReset();
        }}
      >
        <Icon
          name={IconName.Close}
          color={'palette_blue.100'}
          data-testid="document-input-close-icon"
        />
      </span>

      <Icon
        name={IconName.FileUploadedIcon}
        color={'palette_blue.100'}
        width={'3em'}
        height={'3em'}
      />
      <Paragraph data-testid="document-file-name">{currentFilename}</Paragraph>
    </>
  );
}
